import { useEffect, useState } from 'react';
import type { TransformationType, TransformationParams } from '@types';

interface TransformationSelectorProps {
  transformationType: TransformationType;
  transformationParams: TransformationParams;
  onChange: (type: TransformationType, params: TransformationParams) => void;
  sourceValue?: string;
}

const transformationTypes: {
  value: TransformationType;
  label: string;
  color: string;
}[] = [
  { value: 'none', label: 'None', color: 'text-gray-600' },
  { value: 'uppercase', label: 'Uppercase', color: 'text-blue-600' },
  { value: 'lowercase', label: 'Lowercase', color: 'text-blue-600' },
  { value: 'title_case', label: 'Title Case', color: 'text-blue-600' },
  { value: 'trim', label: 'Trim Whitespace', color: 'text-green-600' },
  { value: 'date_format', label: 'Date Format', color: 'text-purple-600' },
  { value: 'number_format', label: 'Number Format', color: 'text-purple-600' },
  { value: 'replace', label: 'Replace', color: 'text-orange-600' },
  { value: 'prefix', label: 'Add Prefix', color: 'text-pink-600' },
  { value: 'suffix', label: 'Add Suffix', color: 'text-pink-600' },
  { value: 'concatenate', label: 'Concatenate', color: 'text-indigo-600' },
  { value: 'split', label: 'Split', color: 'text-red-600' },
];

export default function TransformationSelector({
  transformationType,
  transformationParams,
  onChange,
  sourceValue = '',
}: TransformationSelectorProps) {
  const [localParams, setLocalParams] = useState(transformationParams || {});
  const [preview, setPreview] = useState<string>('');

  const applyTransformation = (
    value: string,
    type: TransformationType,
    params: TransformationParams
  ): string => {
    if (!value) return '';

    try {
      switch (type) {
        case 'none':
          return value;
        case 'uppercase':
          return value.toUpperCase();
        case 'lowercase':
          return value.toLowerCase();
        case 'title_case':
          return value
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        case 'trim':
          return value.trim();
        case 'date_format':
          return params.pattern ? `[Date: ${params.pattern}]` : value;
        case 'number_format':
          return params.decimals
            ? parseFloat(value).toFixed(params.decimals)
            : value;
        case 'replace':
          return params.old_value && params.new_value
            ? value.replace(new RegExp(params.old_value, 'g'), params.new_value)
            : value;
        case 'prefix':
          return params.value ? `${params.value}${value}` : value;
        case 'suffix':
          return params.value ? `${value}${params.value}` : value;
        case 'concatenate':
          return params.separator ? `${value}${params.separator}[...]` : value;
        case 'split':
          if (params.delimiter && params.index !== undefined) {
            const parts = value.split(params.delimiter);
            return parts[params.index] || value;
          }
          return value;
        default:
          return value;
      }
    } catch {
      return value;
    }
  };

  // Sync local params with prop changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalParams(transformationParams || {});
  }, [transformationParams]);

  // Update preview when inputs change
  useEffect(() => {
    if (sourceValue) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPreview(
        applyTransformation(sourceValue, transformationType, localParams)
      );
    }
  }, [sourceValue, transformationType, localParams]);

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as TransformationType;
    onChange(newType, {});
    setLocalParams({});
  };

  const handleParamChange = (key: string, value: string | number) => {
    const newParams = { ...localParams, [key]: value };
    setLocalParams(newParams);
    onChange(transformationType, newParams);
  };

  const selectedType = transformationTypes.find(
    t => t.value === transformationType
  );

  return (
    <div className="space-y-3">
      {/* Transformation Type Selector */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Transformation
        </label>
        <select
          value={transformationType}
          onChange={handleTypeChange}
          className={`w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm ${
            selectedType?.color || ''
          }`}
        >
          {transformationTypes.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Conditional Parameter Inputs */}
      {transformationType === 'date_format' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Format Pattern
          </label>
          <input
            type="text"
            value={localParams.pattern || ''}
            onChange={e => handleParamChange('pattern', e.target.value)}
            placeholder="e.g., YYYY-MM-DD"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Common formats: YYYY-MM-DD, MM/DD/YYYY, DD-MMM-YYYY
          </p>
        </div>
      )}

      {transformationType === 'number_format' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Decimal Places
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={localParams.decimals || 2}
            onChange={e =>
              handleParamChange('decimals', parseInt(e.target.value))
            }
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
        </div>
      )}

      {transformationType === 'replace' && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Find
            </label>
            <input
              type="text"
              value={localParams.old_value || ''}
              onChange={e => handleParamChange('old_value', e.target.value)}
              placeholder="Text to find"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Replace with
            </label>
            <input
              type="text"
              value={localParams.new_value || ''}
              onChange={e => handleParamChange('new_value', e.target.value)}
              placeholder="Replacement text"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>
        </>
      )}

      {(transformationType === 'prefix' || transformationType === 'suffix') && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {transformationType === 'prefix' ? 'Prefix' : 'Suffix'} Value
          </label>
          <input
            type="text"
            value={localParams.value || ''}
            onChange={e => handleParamChange('value', e.target.value)}
            placeholder={`Value to ${transformationType === 'prefix' ? 'add before' : 'add after'}`}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
        </div>
      )}

      {transformationType === 'concatenate' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Separator
          </label>
          <input
            type="text"
            value={localParams.separator || ''}
            onChange={e => handleParamChange('separator', e.target.value)}
            placeholder="e.g., -, _, space"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
        </div>
      )}

      {transformationType === 'split' && (
        <>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Delimiter
            </label>
            <input
              type="text"
              value={localParams.delimiter || ''}
              onChange={e => handleParamChange('delimiter', e.target.value)}
              placeholder="e.g., comma, space, -"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Index (0-based)
            </label>
            <input
              type="number"
              min="0"
              value={localParams.index ?? 0}
              onChange={e =>
                handleParamChange('index', parseInt(e.target.value))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>
        </>
      )}

      {/* Preview */}
      {sourceValue && transformationType !== 'none' && (
        <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Preview
          </label>
          <div className="text-sm">
            <div className="text-gray-500">
              Input: <span className="font-mono">{sourceValue}</span>
            </div>
            <div className="text-gray-900 mt-1">
              Output: <span className="font-mono font-medium">{preview}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
